import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DryCleaningOutlinedIcon from "@mui/icons-material/DryCleaningOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import TreeView from "react-treeview";
import "react-treeview/react-treeview.css";
import "../../css/productTree.css";
import { SidebarContext } from "../../components/Sidebar/SidebarProvider";
import { materialFotoPublicUrl } from "../../api/Materialoteca/MaterialotecaApi";
import {
  createFeria,
  createFeriaEdicion,
  deleteFeria,
  deleteFeriaEdicion,
  getFeriaEdicion,
  getFerias,
} from "../../api/Ferias/FeriasApi";
import FeriaEdicionPanel from "./FeriaEdicionPanel";
import FeriaEstadoChip from "./FeriaEstadoChip";
import { materialPrimaryFotoArchivo } from "./feriaStatus";
import MaterialotecaPhotoLightbox from "../Materialoteca/MaterialotecaPhotoLightbox";

const ACCENT = "#ffac7e";
const TREE_ICON = "#90b2b7";
/** Misma altura para cabecera del árbol y cabecera de la edición. */
const HEADER_HEIGHT = 104;

const apiErrorMessage = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const fromApi =
    (typeof data === "string" && data) ||
    data?.error ||
    data?.message ||
    err?.message;
  if (status === 404) {
    return `${fallback} (404). El API remoto aún no tiene /api/ferias: reinicia/despliega el BACK o usa localhost:3002.`;
  }
  if (status) return `${fallback} (${status})${fromApi ? `: ${fromApi}` : ""}`;
  return fromApi || fallback;
};

const fairKey = (id) => `fair-${id}`;
const editionKey = (id) => `ed-${id}`;
const suppliersKey = (id) => `ed-${id}-sup`;
const materialsKey = (id) => `ed-${id}-mat`;

const leafLabelSx = (theme, selected) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  cursor: "pointer",
  fontWeight: selected ? 700 : 600,
  fontSize: "0.85em",
  padding: "0.55em 0.65em",
  borderRadius: selected ? "6px" : "4px",
  width: "98%",
  bgcolor: selected ? theme.palette.primary.main : "transparent",
  color: selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.secondary,
  "&:hover": {
    bgcolor: selected
      ? theme.palette.primary.main
      : alpha(theme.palette.primary.main, 0.08),
  },
});

const branchLabelSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  padding: "0.55em 0.35em",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "0.9em",
  width: "100%",
};

const Ferias = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { contentMargin } = useContext(SidebarContext);

  const [ferias, setFerias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedEditionId, setSelectedEditionId] = useState(null);
  const [selectedFocus, setSelectedFocus] = useState("overview");
  /** id_FeriaProveedor o id_FeriaMaterial según selectedFocus */
  const [selectedFocusId, setSelectedFocusId] = useState(null);
  const [editionCache, setEditionCache] = useState({});
  const [mobileDetailView, setMobileDetailView] = useState(false);

  const [fairOpen, setFairOpen] = useState(false);
  const [editionOpen, setEditionOpen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [fairForm, setFairForm] = useState({
    Nombre: "",
    Ciudad: "",
    Pais: "",
    Notas: "",
  });
  const [editionForm, setEditionForm] = useState({
    Anio: new Date().getFullYear(),
    Temporada: "",
    FechaInicio: "",
    FechaFin: "",
    Asistimos: true,
    Notas: "",
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [photoLightboxSrc, setPhotoLightboxSrc] = useState(null);
  const [photoLightboxAlt, setPhotoLightboxAlt] = useState("");

  const editionIsEmpty = (ed) =>
    Number(ed?.Asistentes || 0) === 0 &&
    Number(ed?.Proveedores || 0) === 0 &&
    Number(ed?.Materiales || 0) === 0;

  const fairCanDelete = (feria) =>
    (feria?.ediciones || []).every((ed) => editionIsEmpty(ed));

  const openDeleteFair = (feria, e) => {
    e?.stopPropagation?.();
    setDeleteError("");
    if (!fairCanDelete(feria)) {
      setDeleteTarget({ type: "fair-blocked", feria });
      return;
    }
    setDeleteTarget({ type: "fair", feria });
  };

  const openDeleteEdition = (feria, ed, e) => {
    e?.stopPropagation?.();
    setDeleteError("");
    if (!editionIsEmpty(ed)) {
      setDeleteTarget({ type: "edition-blocked", feria, edicion: ed });
      return;
    }
    setDeleteTarget({ type: "edition", feria, edicion: ed });
  };

  const handleConfirmDelete = async () => {
    if (
      !deleteTarget ||
      deleteTarget.type === "edition-blocked" ||
      deleteTarget.type === "fair-blocked"
    )
      return;
    setDeleting(true);
    setDeleteError("");
    try {
      if (deleteTarget.type === "fair") {
        await deleteFeria(deleteTarget.feria.id_Feria);
        if (
          (deleteTarget.feria.ediciones || []).some(
            (ed) => Number(ed.id_FeriaEdicion) === Number(selectedEditionId),
          )
        ) {
          setSelectedEditionId(null);
          setSelectedFocus("overview");
        }
      } else if (deleteTarget.type === "edition") {
        const id = deleteTarget.edicion.id_FeriaEdicion;
        await deleteFeriaEdicion(id);
        if (Number(selectedEditionId) === Number(id)) {
          setSelectedEditionId(null);
          setSelectedFocus("overview");
        }
        setEditionCache((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
      setDeleteTarget(null);
      await load();
    } catch (err) {
      console.error("❌ delete feria/edicion:", err);
      setDeleteError(apiErrorMessage(err, t("FeriasPage.deleteError")));
    } finally {
      setDeleting(false);
    }
  };

  const load = async () => {
    setLoadError("");
    try {
      const data = await getFerias();
      setFerias(Array.isArray(data) ? data : []);
      if (selectedEditionId) {
        try {
          const detail = await getFeriaEdicion(selectedEditionId);
          setEditionCache((prev) => ({
            ...prev,
            [selectedEditionId]: detail,
          }));
        } catch {
          /* ignore */
        }
      }
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error("❌ Ferias:", err);
      setFerias([]);
      setLoadError(apiErrorMessage(err, t("FeriasPage.loadError")));
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const expandEdicionId = location.state?.expandEdicionId;
    if (!expandEdicionId || loading || ferias.length === 0) return;

    const idNum = Number(expandEdicionId);
    const owner = ferias.find((f) =>
      (f.ediciones || []).some((e) => Number(e.id_FeriaEdicion) === idNum),
    );
    if (!owner) return;

    setExpandedNodes((prev) => ({
      ...prev,
      [fairKey(owner.id_Feria)]: true,
      [editionKey(idNum)]: true,
    }));
    setSelectedEditionId(idNum);
    setSelectedFocus("overview");
    ensureEditionDetail(idNum);
    if (isMobile) setMobileDetailView(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, loading, ferias, navigate, location.pathname, isMobile]);

  const filteredFerias = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return ferias;
    return ferias
      .map((f) => {
        const fairHit =
          (f.Nombre || "").toLowerCase().includes(q) ||
          (f.Ciudad || "").toLowerCase().includes(q) ||
          (f.Pais || "").toLowerCase().includes(q);
        const eds = (f.ediciones || []).filter(
          (e) =>
            fairHit ||
            String(e.Anio).includes(q) ||
            (e.Temporada || "").toLowerCase().includes(q),
        );
        if (!fairHit && eds.length === 0) return null;
        return { ...f, ediciones: fairHit ? f.ediciones || [] : eds };
      })
      .filter(Boolean);
  }, [ferias, searchTerm]);

  const toggleNode = (key) => {
    setExpandedNodes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const ensureEditionDetail = async (edicionId, { force = false } = {}) => {
    if (!force && editionCache[edicionId]) return editionCache[edicionId];
    try {
      const detail = await getFeriaEdicion(edicionId);
      setEditionCache((prev) => ({ ...prev, [edicionId]: detail }));
      return detail;
    } catch (err) {
      console.error("❌ getFeriaEdicion tree:", err);
      return null;
    }
  };

  const selectEdition = (
    feriaId,
    edicionId,
    focus = "overview",
    focusId = null,
  ) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [fairKey(feriaId)]: true,
      [editionKey(edicionId)]: true,
      ...(focus === "suppliers" || focus === "materials"
        ? {
            [suppliersKey(edicionId)]: true,
            ...(focus === "materials"
              ? { [materialsKey(edicionId)]: true }
              : {}),
          }
        : {}),
    }));
    setSelectedEditionId(edicionId);
    setSelectedFocus(focus);
    setSelectedFocusId(focusId != null ? Number(focusId) : null);
    ensureEditionDetail(edicionId);
    if (isMobile) setMobileDetailView(true);
  };

  const toggleEditionExpand = (feriaId, edicionId) => {
    const key = editionKey(edicionId);
    setExpandedNodes((prev) => {
      const willOpen = !prev[key];
      if (willOpen) ensureEditionDetail(edicionId);
      return {
        ...prev,
        [fairKey(feriaId)]: true,
        [key]: willOpen,
      };
    });
  };

  const emptyEditionForm = () => ({
    Anio: new Date().getFullYear(),
    Temporada: "",
    FechaInicio: "",
    FechaFin: "",
    Asistimos: true,
    Notas: "",
  });

  const handleCreateFair = async () => {
    if (!fairForm.Nombre.trim()) {
      setFormError(t("FeriasPage.nameRequired"));
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const created = await createFeria(fairForm);
      const feriaId = created?.id_Feria;
      let edicionId = null;
      if (feriaId) {
        const ed = await createFeriaEdicion(feriaId, editionForm);
        edicionId = ed?.id_FeriaEdicion || null;
      }
      setFairOpen(false);
      setFairForm({ Nombre: "", Ciudad: "", Pais: "", Notas: "" });
      setEditionForm(emptyEditionForm());
      await load();
      if (feriaId && edicionId) {
        selectEdition(feriaId, edicionId);
      } else if (feriaId) {
        setExpandedNodes((prev) => ({
          ...prev,
          [fairKey(feriaId)]: true,
        }));
      }
    } catch (err) {
      console.error("❌ createFeria:", err);
      setFormError(apiErrorMessage(err, t("FeriasPage.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateEdition = async () => {
    if (!editionOpen) return;
    setSaving(true);
    setFormError("");
    try {
      const feriaId = editionOpen.id_Feria;
      const created = await createFeriaEdicion(feriaId, editionForm);
      const edicionId = created?.id_FeriaEdicion;
      setEditionOpen(null);
      await load();
      if (edicionId) selectEdition(feriaId, edicionId);
    } catch (err) {
      console.error("❌ createFeriaEdicion:", err);
      setFormError(apiErrorMessage(err, t("FeriasPage.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const openNewFair = () => {
    setFormError("");
    setFairForm({ Nombre: "", Ciudad: "", Pais: "", Notas: "" });
    setEditionForm(emptyEditionForm());
    setFairOpen(true);
  };

  const openNewEdition = (feria, e) => {
    e?.stopPropagation?.();
    setFormError("");
    setEditionForm(emptyEditionForm());
    setEditionOpen(feria);
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: alpha(theme.palette.background.paper, 0.9),
    },
  };

  const dialogPaperSx = {
    borderRadius: 3,
    overflow: "hidden",
  };

  const dialogHeader = (title, subtitle) => (
    <Box
      sx={{
        px: 3,
        pt: 2.5,
        pb: 2,
        background: `linear-gradient(125deg, ${alpha(ACCENT, 0.28)} 0%, ${alpha(
          theme.palette.primary.main,
          0.1,
        )} 100%)`,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(ACCENT, 0.4),
            flexShrink: 0,
          }}
        >
          <EventOutlinedIcon fontSize="small" />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );

  const sectionLabel = (text) => (
    <Typography
      variant="overline"
      sx={{
        fontWeight: 800,
        letterSpacing: 0.8,
        color: "text.secondary",
        display: "block",
        mb: 0.5,
      }}
    >
      {text}
    </Typography>
  );

  const editionFields = (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label={t("FeriasPage.labelYear")}
          type="number"
          value={editionForm.Anio}
          onChange={(e) =>
            setEditionForm({
              ...editionForm,
              Anio: Number(e.target.value),
            })
          }
          fullWidth
          disabled={saving}
          sx={fieldSx}
        />
        <TextField
          label={t("FeriasPage.labelSeason")}
          placeholder="SS / AW"
          value={editionForm.Temporada}
          onChange={(e) =>
            setEditionForm({ ...editionForm, Temporada: e.target.value })
          }
          fullWidth
          disabled={saving}
          sx={fieldSx}
        />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label={t("FeriasPage.labelStart")}
          type="date"
          InputLabelProps={{ shrink: true }}
          value={editionForm.FechaInicio}
          onChange={(e) =>
            setEditionForm({
              ...editionForm,
              FechaInicio: e.target.value,
            })
          }
          fullWidth
          disabled={saving}
          sx={fieldSx}
        />
        <TextField
          label={t("FeriasPage.labelEnd")}
          type="date"
          InputLabelProps={{ shrink: true }}
          value={editionForm.FechaFin}
          onChange={(e) =>
            setEditionForm({ ...editionForm, FechaFin: e.target.value })
          }
          fullWidth
          disabled={saving}
          sx={fieldSx}
        />
      </Stack>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
          bgcolor: alpha(ACCENT, 0.08),
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={editionForm.Asistimos}
              onChange={(e) =>
                setEditionForm({
                  ...editionForm,
                  Asistimos: e.target.checked,
                })
              }
              disabled={saving}
            />
          }
          label={t("FeriasPage.attending")}
        />
      </Box>
    </Stack>
  );

  const showTree = !isMobile || !mobileDetailView;
  const showDetail = selectedEditionId && (!isMobile || mobileDetailView);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        ...contentMargin,
        px: { xs: 1.25, sm: 2 },
        pt: { xs: 0.5, md: 1 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 2 },
        boxSizing: "border-box",
        height: { xs: "100dvh", md: "100vh" },
        minHeight: { xs: "100dvh", md: "auto" },
        maxHeight: { md: "100dvh" },
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          gap: 2,
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflow: {
            xs: mobileDetailView ? "auto" : "hidden",
            md: "hidden",
          },
        }}
      >
        {showTree && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: "100%", md: "28em" },
              minWidth: { xs: 0, md: "28em" },
              flex: { xs: 1, md: "0 0 auto" },
              height: { md: "100%" },
              minHeight: 0,
              overflow: "hidden",
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                px: { xs: 1.5, sm: 2 },
                py: 1,
                mb: 1.5,
                height: HEADER_HEIGHT,
                minHeight: HEADER_HEIGHT,
                maxHeight: HEADER_HEIGHT,
                display: "flex",
                alignItems: "center",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                background: `linear-gradient(125deg, ${alpha(ACCENT, 0.22)} 0%, ${alpha(
                  theme.palette.primary.main,
                  0.08,
                )} 100%)`,
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(ACCENT, 0.35),
                  mr: 1.25,
                  flexShrink: 0,
                }}
              >
                <EventOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 1,
                    color: "text.secondary",
                    lineHeight: 1.2,
                    display: "block",
                  }}
                >
                  {t("FeriasPage.heroOverline")}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
                  {t("FeriasPage.heroTitle")}
                </Typography>
              </Box>
              <Tooltip title={t("FeriasPage.newFair")}>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={openNewFair}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <TextField
              size="small"
              fullWidth
              placeholder={t("FeriasPage.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                mb: 1.5,
                flexShrink: 0,
                "& .MuiInputBase-root": {
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 2,
                },
              }}
            />

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                p: 1.25,
                backgroundColor: theme.palette.background.paper,
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 4px 20px rgba(0,0,0,0.35)"
                    : "0 6px 24px rgba(0,0,0,0.06)",
                "& .tree-view": { marginBottom: "0.35em" },
                "& .tree-view_children": {
                  marginLeft: "1.1em",
                  paddingLeft: "0.35em",
                },
                "& .tree-view_arrow": {
                  marginRight: "0.45em",
                  fontSize: "1rem",
                },
              }}
            >
              {loading ? (
                <Box
                  sx={{ p: 2, display: "flex", gap: 1, alignItems: "center" }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    {t("FeriasPage.loading")}
                  </Typography>
                </Box>
              ) : loadError ? (
                <Alert severity="error" sx={{ m: 1 }}>
                  {loadError}
                </Alert>
              ) : filteredFerias.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2 }}
                >
                  {t("FeriasPage.empty")}
                </Typography>
              ) : (
                filteredFerias.map((feria) => {
                  const fKey = fairKey(feria.id_Feria);
                  const fairExpanded = !!expandedNodes[fKey];
                  return (
                    <TreeView
                      key={feria.id_Feria}
                      nodeLabel={
                        <Box
                          sx={branchLabelSx}
                          onClick={() => toggleNode(fKey)}
                        >
                          <AccountTreeOutlinedIcon
                            sx={{ color: TREE_ICON, fontSize: 20 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              noWrap
                              sx={{ fontWeight: 800, fontSize: "0.92rem" }}
                            >
                              {feria.Nombre}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              component="div"
                            >
                              {[feria.Ciudad, feria.Pais]
                                .filter(Boolean)
                                .join(" · ") || t("FeriasPage.noLocation")}
                            </Typography>
                          </Box>
                          <Tooltip title={t("FeriasPage.newEdition")}>
                            <IconButton
                              size="small"
                              onClick={(e) => openNewEdition(feria, e)}
                              sx={{ flexShrink: 0 }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("FeriasPage.deleteFair")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => openDeleteFair(feria, e)}
                              sx={{ flexShrink: 0 }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      collapsed={!fairExpanded}
                    >
                      {(feria.ediciones || []).length === 0 ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", px: 1.5, py: 1 }}
                        >
                          {t("FeriasPage.noEditions")}
                        </Typography>
                      ) : (
                        (feria.ediciones || []).map((ed) => {
                          const eKey = editionKey(ed.id_FeriaEdicion);
                          const edExpanded = !!expandedNodes[eKey];
                          const edSelected =
                            Number(selectedEditionId) ===
                              Number(ed.id_FeriaEdicion) &&
                            selectedFocus === "overview";
                          const detail =
                            editionCache[ed.id_FeriaEdicion] || null;
                          const proveedores = detail?.proveedores || [];
                          const materiales = detail?.materiales || [];
                          const sKey = suppliersKey(ed.id_FeriaEdicion);
                          const mKey = materialsKey(ed.id_FeriaEdicion);

                          return (
                            <TreeView
                              key={ed.id_FeriaEdicion}
                              nodeLabel={
                                <Box
                                  sx={{
                                    ...branchLabelSx,
                                    borderRadius: edSelected ? "6px" : 0,
                                    bgcolor: edSelected
                                      ? alpha(theme.palette.primary.main, 0.12)
                                      : "transparent",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleEditionExpand(
                                      feria.id_Feria,
                                      ed.id_FeriaEdicion,
                                    );
                                    setSelectedEditionId(ed.id_FeriaEdicion);
                                    setSelectedFocus("overview");
                                    ensureEditionDetail(ed.id_FeriaEdicion);
                                    if (isMobile) setMobileDetailView(true);
                                  }}
                                >
                                  <FolderOpenIcon
                                    sx={{ color: TREE_ICON, fontSize: 18 }}
                                  />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      noWrap
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "0.88rem",
                                      }}
                                    >
                                      {ed.Anio}
                                      {ed.Temporada ? ` · ${ed.Temporada}` : ""}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                      component="div"
                                    >
                                      {t("FeriasPage.counts", {
                                        people: Number(ed.Asistentes || 0),
                                        suppliers: Number(ed.Proveedores || 0),
                                        materials: Number(ed.Materiales || 0),
                                      })}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={
                                      ed.Asistimos
                                        ? t("FeriasPage.attending")
                                        : t("FeriasPage.notAttending")
                                    }
                                    sx={{
                                      height: 22,
                                      fontSize: "0.68rem",
                                      fontWeight: 700,
                                      display: { xs: "none", sm: "flex" },
                                      bgcolor: ed.Asistimos
                                        ? alpha("#2e7d32", 0.12)
                                        : alpha("#78909c", 0.12),
                                    }}
                                  />
                                  <Tooltip
                                    title={
                                      editionIsEmpty(ed)
                                        ? t("FeriasPage.deleteEdition")
                                        : t(
                                            "FeriasPage.deleteEditionBlockedHint",
                                          )
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) =>
                                        openDeleteEdition(feria, ed, e)
                                      }
                                      sx={{ flexShrink: 0 }}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              }
                              collapsed={!edExpanded}
                            >
                              <TreeView
                                nodeLabel={
                                  <Box
                                    sx={{
                                      ...branchLabelSx,
                                      fontWeight: 600,
                                      fontSize: "0.85em",
                                      bgcolor:
                                        Number(selectedEditionId) ===
                                          Number(ed.id_FeriaEdicion) &&
                                        selectedFocus === "suppliers"
                                          ? alpha(
                                              theme.palette.primary.main,
                                              0.1,
                                            )
                                          : "transparent",
                                      borderRadius: 1,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleNode(sKey);
                                      selectEdition(
                                        feria.id_Feria,
                                        ed.id_FeriaEdicion,
                                        "suppliers",
                                      );
                                    }}
                                  >
                                    <BusinessOutlinedIcon
                                      sx={{ color: TREE_ICON, fontSize: 17 }}
                                    />
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "0.82rem",
                                        flex: 1,
                                      }}
                                    >
                                      {t("FeriasPage.treeSuppliers")}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {Number(
                                        detail
                                          ? proveedores.length
                                          : ed.Proveedores || 0,
                                      )}
                                    </Typography>
                                  </Box>
                                }
                                collapsed={!expandedNodes[sKey]}
                              >
                                {!detail ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ px: 1.5, py: 0.75, display: "block" }}
                                  >
                                    {t("FeriasPage.loadingEdition")}
                                  </Typography>
                                ) : proveedores.length === 0 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ px: 1.5, py: 0.75, display: "block" }}
                                  >
                                    {t("FeriasPage.treeEmptySuppliers")}
                                  </Typography>
                                ) : (
                                  proveedores.map((row) => {
                                    const leafSelected =
                                      Number(selectedEditionId) ===
                                        Number(ed.id_FeriaEdicion) &&
                                      selectedFocus === "suppliers" &&
                                      Number(selectedFocusId) ===
                                        Number(row.id_FeriaProveedor);
                                    return (
                                      <Box
                                        key={row.id_FeriaProveedor}
                                        sx={leafLabelSx(theme, leafSelected)}
                                        onClick={() =>
                                          selectEdition(
                                            feria.id_Feria,
                                            ed.id_FeriaEdicion,
                                            "suppliers",
                                            row.id_FeriaProveedor,
                                          )
                                        }
                                      >
                                        <Typography
                                          noWrap
                                          sx={{ fontSize: "0.8rem" }}
                                        >
                                          {row.proveedor?.N_PROVEEDOR ||
                                            row.fabrica?.NombreFabrica ||
                                            t("FeriasPage.unnamed")}
                                        </Typography>
                                      </Box>
                                    );
                                  })
                                )}
                              </TreeView>

                              <TreeView
                                nodeLabel={
                                  <Box
                                    sx={{
                                      ...branchLabelSx,
                                      fontWeight: 600,
                                      fontSize: "0.85em",
                                      bgcolor:
                                        Number(selectedEditionId) ===
                                          Number(ed.id_FeriaEdicion) &&
                                        selectedFocus === "materials"
                                          ? alpha(
                                              theme.palette.primary.main,
                                              0.1,
                                            )
                                          : "transparent",
                                      borderRadius: 1,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleNode(mKey);
                                      selectEdition(
                                        feria.id_Feria,
                                        ed.id_FeriaEdicion,
                                        "materials",
                                      );
                                    }}
                                  >
                                    <DryCleaningOutlinedIcon
                                      sx={{ color: TREE_ICON, fontSize: 17 }}
                                    />
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "0.82rem",
                                        flex: 1,
                                      }}
                                    >
                                      {t("FeriasPage.treeMaterials")}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {Number(
                                        detail
                                          ? materiales.length
                                          : ed.Materiales || 0,
                                      )}
                                    </Typography>
                                  </Box>
                                }
                                collapsed={!expandedNodes[mKey]}
                              >
                                {!detail ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ px: 1.5, py: 0.75, display: "block" }}
                                  >
                                    {t("FeriasPage.loadingEdition")}
                                  </Typography>
                                ) : materiales.length === 0 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ px: 1.5, py: 0.75, display: "block" }}
                                  >
                                    {t("FeriasPage.treeEmptyMaterials")}
                                  </Typography>
                                ) : (
                                  materiales.map((row) => {
                                    const thumb = materialFotoPublicUrl(
                                      materialPrimaryFotoArchivo(row.material),
                                    );
                                    const leafSelected =
                                      Number(selectedEditionId) ===
                                        Number(ed.id_FeriaEdicion) &&
                                      selectedFocus === "materials" &&
                                      Number(selectedFocusId) ===
                                        Number(row.id_FeriaMaterial);
                                    return (
                                      <Box
                                        key={row.id_FeriaMaterial}
                                        sx={{
                                          ...leafLabelSx(theme, leafSelected),
                                          justifyContent: "space-between",
                                          gap: 0.75,
                                        }}
                                        onClick={() =>
                                          selectEdition(
                                            feria.id_Feria,
                                            ed.id_FeriaEdicion,
                                            "materials",
                                            row.id_FeriaMaterial,
                                          )
                                        }
                                      >
                                        <Box
                                          onClick={(e) => {
                                            if (!thumb) return;
                                            e.stopPropagation();
                                            setPhotoLightboxSrc(thumb);
                                            setPhotoLightboxAlt(
                                              row.material?.NombreMaterial ||
                                                "",
                                            );
                                          }}
                                          sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: 1,
                                            flexShrink: 0,
                                            overflow: "hidden",
                                            bgcolor: leafSelected
                                              ? alpha("#fff", 0.2)
                                              : alpha(
                                                  theme.palette.text.primary,
                                                  0.06,
                                                ),
                                            border: `1px solid ${alpha(
                                              theme.palette.divider,
                                              0.8,
                                            )}`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: thumb
                                              ? "zoom-in"
                                              : "default",
                                          }}
                                        >
                                          {thumb ? (
                                            <Box
                                              component="img"
                                              src={thumb}
                                              alt=""
                                              sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                              }}
                                            />
                                          ) : (
                                            <DryCleaningOutlinedIcon
                                              sx={{
                                                fontSize: 16,
                                                color: leafSelected
                                                  ? "inherit"
                                                  : "text.disabled",
                                              }}
                                            />
                                          )}
                                        </Box>
                                        <Typography
                                          noWrap
                                          sx={{
                                            fontSize: "0.8rem",
                                            flex: 1,
                                            minWidth: 0,
                                          }}
                                        >
                                          {row.material?.NombreMaterial ||
                                            `#${row.id_Materialoteca}`}
                                        </Typography>
                                        <FeriaEstadoChip estado={row.Estado} />
                                      </Box>
                                    );
                                  })
                                )}
                              </TreeView>
                            </TreeView>
                          );
                        })
                      )}
                    </TreeView>
                  );
                })
              )}
            </Box>

            {isMobile && !selectedEditionId ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexShrink: 0, textAlign: "center", py: 1, px: 1 }}
              >
                {t("FeriasPage.tapEditionHint")}
              </Typography>
            ) : null}
          </Box>
        )}

        {showDetail ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: { xs: "auto", md: 0 },
              height: { md: "100%" },
              overflow: "hidden",
            }}
          >
            {isMobile ? (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => setMobileDetailView(false)}
                sx={{
                  alignSelf: "flex-start",
                  mb: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              >
                {t("FeriasPage.backToTree")}
              </Button>
            ) : null}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                pt: 0,
              }}
            >
              <FeriaEdicionPanel
                edicionId={selectedEditionId}
                showHeader
                headerHeight={HEADER_HEIGHT}
                focusSection={selectedFocus}
                focusItemId={selectedFocusId}
                onFocusItem={(focus, focusId) => {
                  setSelectedFocus(focus);
                  setSelectedFocusId(focusId != null ? Number(focusId) : null);
                }}
                onChanged={load}
              />
            </Box>
          </Box>
        ) : (
          !isMobile && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 3,
                bgcolor: alpha(theme.palette.text.primary, 0.02),
                px: 3,
              }}
            >
              <Typography color="text.secondary" textAlign="center">
                {t("FeriasPage.selectEditionHint")}
              </Typography>
            </Box>
          )
        )}
      </Box>

      <Dialog
        open={fairOpen}
        onClose={() => {
          if (!saving) {
            setFairOpen(false);
            setFormError("");
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        {dialogHeader(
          t("FeriasPage.newFair"),
          t("FeriasPage.newFairWithEditionHint"),
        )}
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Stack spacing={2.5}>
            {formError ? (
              <Alert severity="error" onClose={() => setFormError("")}>
                {formError}
              </Alert>
            ) : null}

            {sectionLabel(t("FeriasPage.sectionFair"))}
            <TextField
              label={t("FeriasPage.labelName")}
              value={fairForm.Nombre}
              onChange={(e) =>
                setFairForm({ ...fairForm, Nombre: e.target.value })
              }
              fullWidth
              required
              autoFocus
              disabled={saving}
              sx={fieldSx}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("FeriasPage.labelCity")}
                value={fairForm.Ciudad}
                onChange={(e) =>
                  setFairForm({ ...fairForm, Ciudad: e.target.value })
                }
                fullWidth
                disabled={saving}
                sx={fieldSx}
              />
              <TextField
                label={t("FeriasPage.labelCountry")}
                value={fairForm.Pais}
                onChange={(e) =>
                  setFairForm({ ...fairForm, Pais: e.target.value })
                }
                fullWidth
                disabled={saving}
                sx={fieldSx}
              />
            </Stack>
            <TextField
              label={t("FeriasPage.labelNotes")}
              value={fairForm.Notas}
              onChange={(e) =>
                setFairForm({ ...fairForm, Notas: e.target.value })
              }
              fullWidth
              multiline
              minRows={2}
              disabled={saving}
              sx={fieldSx}
            />

            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              {sectionLabel(t("FeriasPage.sectionFirstEdition"))}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.75 }}
              >
                {t("FeriasPage.firstEditionHelp")}
              </Typography>
              {editionFields}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setFairOpen(false);
              setFormError("");
            }}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("FeriasPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateFair}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.5,
              bgcolor: ACCENT,
              color: theme.palette.getContrastText(ACCENT),
              boxShadow: "none",
              "&:hover": { bgcolor: "#e89566", boxShadow: "none" },
            }}
          >
            {t("FeriasPage.saveFairAndEdition")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editionOpen)}
        onClose={() => {
          if (!saving) {
            setEditionOpen(null);
            setFormError("");
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        {dialogHeader(t("FeriasPage.newEdition"), editionOpen?.Nombre || "")}
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Stack spacing={2}>
            {formError ? (
              <Alert severity="error" onClose={() => setFormError("")}>
                {formError}
              </Alert>
            ) : (
              <Alert
                severity="info"
                sx={{ borderRadius: 2, alignItems: "center" }}
              >
                {t("FeriasPage.editionNextStep")}
              </Alert>
            )}
            {editionFields}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setEditionOpen(null);
              setFormError("");
            }}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("FeriasPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateEdition}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.5,
              bgcolor: ACCENT,
              color: theme.palette.getContrastText(ACCENT),
              boxShadow: "none",
              "&:hover": { bgcolor: "#e89566", boxShadow: "none" },
            }}
          >
            {t("FeriasPage.saveAndOpen")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) {
            setDeleteTarget(null);
            setDeleteError("");
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Stack spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(
                  deleteTarget?.type === "edition-blocked" ||
                    deleteTarget?.type === "fair-blocked"
                    ? theme.palette.warning.main
                    : theme.palette.error.main,
                  0.12,
                ),
              }}
            >
              <WarningAmberOutlinedIcon
                color={
                  deleteTarget?.type === "edition-blocked" ||
                  deleteTarget?.type === "fair-blocked"
                    ? "warning"
                    : "error"
                }
              />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1.1rem" }}>
              {deleteTarget?.type === "fair"
                ? t("FeriasPage.deleteFairTitle")
                : deleteTarget?.type === "fair-blocked"
                  ? t("FeriasPage.deleteFairBlockedTitle")
                  : deleteTarget?.type === "edition-blocked"
                    ? t("FeriasPage.deleteEditionBlockedTitle")
                    : t("FeriasPage.deleteEditionTitle")}
            </Typography>
            {deleteTarget?.type === "fair" ? (
              <Typography variant="body2" color="text.secondary">
                {t("FeriasPage.deleteFairBody", {
                  name: deleteTarget.feria?.Nombre,
                  count: (deleteTarget.feria?.ediciones || []).length,
                })}
              </Typography>
            ) : null}
            {deleteTarget?.type === "fair-blocked" ? (
              <Typography variant="body2" color="text.secondary">
                {t("FeriasPage.deleteFairBlockedBody", {
                  name: deleteTarget.feria?.Nombre,
                })}
              </Typography>
            ) : null}
            {deleteTarget?.type === "edition" ? (
              <Typography variant="body2" color="text.secondary">
                {t("FeriasPage.deleteEditionBody", {
                  fair: deleteTarget.feria?.Nombre,
                  year: deleteTarget.edicion?.Anio,
                  season: deleteTarget.edicion?.Temporada
                    ? ` · ${deleteTarget.edicion.Temporada}`
                    : "",
                })}
              </Typography>
            ) : null}
            {deleteTarget?.type === "edition-blocked" ? (
              <Typography variant="body2" color="text.secondary">
                {t("FeriasPage.deleteEditionBlockedBody", {
                  year: deleteTarget.edicion?.Anio,
                  people: Number(deleteTarget.edicion?.Asistentes || 0),
                  suppliers: Number(deleteTarget.edicion?.Proveedores || 0),
                  materials: Number(deleteTarget.edicion?.Materiales || 0),
                })}
              </Typography>
            ) : null}
            {deleteError ? (
              <Alert severity="error" sx={{ width: "100%" }}>
                {deleteError}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteTarget(null);
              setDeleteError("");
            }}
            disabled={deleting}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {deleteTarget?.type === "edition-blocked" ||
            deleteTarget?.type === "fair-blocked"
              ? t("FeriasPage.close")
              : t("FeriasPage.cancel")}
          </Button>
          {deleteTarget?.type !== "edition-blocked" &&
          deleteTarget?.type !== "fair-blocked" ? (
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deleting}
              startIcon={
                deleting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              {t("FeriasPage.confirmDelete")}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <MaterialotecaPhotoLightbox
        open={Boolean(photoLightboxSrc)}
        src={photoLightboxSrc}
        alt={photoLightboxAlt}
        onClose={() => {
          setPhotoLightboxSrc(null);
          setPhotoLightboxAlt("");
        }}
        closeAriaLabel={t("MaterialotecaPage.photoLightboxCloseAria")}
      />
    </Box>
  );
};

export default Ferias;
